<template>
  <div id="app-root" :data-theme="theme">
    <!-- Navigation — shown only when authenticated and not on admin routes -->
    <nav v-if="showNav" class="app-nav">
      <div class="app-nav-inner">
        <RouterLink :to="companyPath('/dashboard')" class="nav-brand">
          <img src="/logo.png" alt="" class="nav-logo" />
          {{ portalName }}
        </RouterLink>

        <button class="nav-toggle" @click="mobileNavOpen = !mobileNavOpen" aria-label="Toggle navigation">
          <span class="nav-toggle-bar" />
          <span class="nav-toggle-bar" />
          <span class="nav-toggle-bar" />
        </button>

        <div class="nav-links" :class="{ open: mobileNavOpen }">
          <RouterLink :to="companyPath('/dashboard')" class="nav-link" @click="mobileNavOpen = false">Dashboard</RouterLink>
          <RouterLink :to="companyPath('/deals')" class="nav-link" @click="mobileNavOpen = false">Deals</RouterLink>
          <RouterLink :to="companyPath('/proofs')" class="nav-link" @click="mobileNavOpen = false">Proofs</RouterLink>
          <RouterLink :to="companyPath('/accounts')" class="nav-link" @click="mobileNavOpen = false">Advertisers</RouterLink>
          <RouterLink :to="companyPath('/orders/new')" class="nav-link" @click="mobileNavOpen = false">New Order</RouterLink>
          <RouterLink :to="companyPath('/orders/book-package')" class="nav-link" @click="mobileNavOpen = false">Book Package</RouterLink>
        </div>

        <GlobalSearch />

        <div class="nav-spacer" />

        <div class="nav-user">
          <span>{{ repName }}</span>
          <button class="btn btn-ghost btn-sm" style="color: var(--nav-text);" @click="logout">
            Sign out
          </button>
        </div>
      </div>
    </nav>

    <RouterView />
  </div>
</template>

<script setup lang="ts">
import { companyPath } from '@/utils/companyNavigation'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { getTheme, getConfig, getSessionTokenRef } from '@/api/config'
import { beaconLogout, keepSessionAlive } from '@/api/salesRepApi'
import GlobalSearch from '@/components/GlobalSearch.vue'
import { startSessionTabTracking, stopSessionTabTracking } from '@/utils/sessionTabs'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const mobileNavOpen = ref(false)
const sessionToken = getSessionTokenRef()
let leaseTimer: ReturnType<typeof setInterval> | undefined

const theme = computed(() => getTheme())
const portalName = computed(() => getConfig()?.portalName ?? 'SalesScout')
const repName = computed(() => authStore.repName)

const isAdminRoute = computed(() => !!route.meta.isAdmin)
const showNav = computed(() => authStore.isAuthenticated && !isAdminRoute.value)

async function logout() {
  await authStore.logout()
  router.push(companyPath('/login'))
}

// Release the license seat only when the last live SalesScout tab closes.
// navigator.sendBeacon is fire-and-forget and works even during page unload.
function handleBeforeUnload() {
  if (authStore.isAuthenticated && sessionToken.value) {
    const isLastTab = stopSessionTabTracking(sessionToken.value)
    if (isLastTab) {
      beaconLogout(sessionToken.value)
    }
  }
}

onMounted(() => {
  // Apply theme immediately
  document.documentElement.setAttribute('data-theme', theme.value)
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  stopSessionTabTracking()
  clearInterval(leaseTimer)
})

watch(theme, (t) => {
  document.documentElement.setAttribute('data-theme', t)
})

watch(
  [() => authStore.isAuthenticated, sessionToken],
  ([isAuthenticated, token]) => {
    clearInterval(leaseTimer)
    if (isAuthenticated && token) {
      startSessionTabTracking(token)
      keepSessionAlive()
      leaseTimer = setInterval(keepSessionAlive, 20000)
      return
    }

    stopSessionTabTracking()
  },
  { immediate: true }
)
</script>
